
import { Box, Card, CardMedia, IconButton, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import React, { useCallback, useRef, useState } from "react"
import { AddCircleRounded, CameraAltRounded, CloseRounded, DeleteRounded } from "resource/muiImport"
import { grey } from "@mui/material/colors"
import dayjs from "dayjs"

const FilePreviewCard = ({ filesData, setFilesData, pendingFiles, setPendingFiles, onDeleteServerFile, fileIdKey, customComponent, isReadOnly }) => {

  const [selectedImage, setSelectedImage] = useState(null)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files)
    const newFiles = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }))
    setPendingFiles(o => [...o, ...newFiles])
    e.target.value = null
  }, [setPendingFiles])

  const handleRemovePendingFile = useCallback((index) => (e) => {
    e.stopPropagation()
    setPendingFiles(o => {
      URL.revokeObjectURL(o[index].preview)
      return o.filter((_, i) => i !== index)
    })
    setSelectedImage(null)
  }, [setPendingFiles])

  const handleDeleteFile = useCallback((file) => (e) => {
    e.stopPropagation()
    onDeleteServerFile(file).then(() => {
      setFilesData(o => o.filter(f => f[fileIdKey] !== file[fileIdKey]))
      setSelectedImage(null)
    })
  }, [onDeleteServerFile, setFilesData, fileIdKey])

  return (
    <>
      <BoxFC sx={{ gap: 0 }}>
        <BoxFR>
          <Typography fontWeight="bold">ไฟล์แนบ ({filesData.length + pendingFiles.length})</Typography>
          {!isReadOnly && <>
            <IconButton sx={{ p: 0 }} onClick={() => fileInputRef.current?.click()}>
              <AddCircleRounded color="primary" />
            </IconButton>
            <IconButton sx={{ p: 0 }} onClick={() => cameraInputRef.current?.click()}>
              <CameraAltRounded color="secondary" />
            </IconButton>
          </>}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            hidden
            onChange={handleFileSelect}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleFileSelect}
          />
          <Box flex={1} />
          {customComponent}
        </BoxFR>
        <Box sx={{ overflowX: 'auto', mt: 1 }}>
          <BoxFR sx={{ gap: 1, p: 0 }}>
            {filesData.map((file) => (
              <Card
                key={file[fileIdKey]}
                sx={{
                  width: 100,
                  height: 100,
                  cursor: 'pointer',
                  position: 'relative',
                  flexShrink: 0,
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => setSelectedImage({ ...file, type: 'server' })}
              >
                <CardMedia
                  component="img"
                  height="100"
                  image={file.Url}
                  alt={file.Label}
                  sx={{ objectFit: 'cover' }}
                />
              </Card>
            ))}
            {pendingFiles.map((file, index) => (
              <Card
                key={`pending-${index}`}
                sx={{
                  width: 100,
                  height: 100,
                  cursor: 'pointer',
                  position: 'relative',
                  flexShrink: 0,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => setSelectedImage({ ...file, type: 'pending', index })}
              >
                <CardMedia
                  component="img"
                  height="100"
                  image={file.preview}
                  alt={file.name}
                  sx={{ objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: 'error.main',
                    color: 'white',
                    p: 0.3,
                    '&:hover': { bgcolor: 'error.dark' }
                  }}
                  onClick={handleRemovePendingFile(index)}
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Card>
            ))}
            {filesData.length === 0 && pendingFiles.length === 0 && (
              <Box
                sx={{
                  width: '100%',
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: grey[200],
                  borderRadius: 1
                }}
              >
                <Typography color="text.secondary">ไม่มีไฟล์แนบ</Typography>
              </Box>
            )}
          </BoxFR>
        </Box>
      </BoxFC>

      {/* Full screen image preview */}
      {selectedImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={() => setSelectedImage(null)}
        >
          <IconButton
            sx={{ position: 'absolute', top: 16, right: 16, color: 'white' }}
            onClick={() => setSelectedImage(null)}
          >
            <CloseRounded />
          </IconButton>
          {selectedImage.type === 'server' && !isReadOnly && (
            <IconButton
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                color: 'white',
                bgcolor: 'error.main',
                '&:hover': { bgcolor: 'error.dark' }
              }}
              onClick={handleDeleteFile(selectedImage)}
            >
              <DeleteRounded />
            </IconButton>
          )}
          {selectedImage.type === 'pending' && (
            <IconButton
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                color: 'white',
                bgcolor: 'error.main',
                '&:hover': { bgcolor: 'error.dark' }
              }}
              onClick={handleRemovePendingFile(selectedImage.index)}
            >
              <DeleteRounded />
            </IconButton>
          )}
          <BoxFC
            sx={{
              position: 'absolute',
              width: '80%',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              py: 1,
              borderRadius: 1,
              gap: 0,
              alignItems: 'center'
            }}
          >
            <Typography>{selectedImage.Label || selectedImage.name || ''}</Typography>
            {(selectedImage.FName || selectedImage.LName || selectedImage.NName) && (
              <Typography variant="caption">
                {selectedImage.ModDte ? dayjs(selectedImage.ModDte).format("YYYY-MM-DD HH:mm:ss ") : ''}{`${selectedImage.FName || ''} ${selectedImage.LName || ''}${selectedImage.NName ? ` (${selectedImage.NName})` : ''}`.trim()}
              </Typography>
            )}
            {selectedImage.ModDte && (
              <Typography variant="caption"></Typography>
            )}
          </BoxFC>
          <Box
            component="img"
            src={selectedImage.Url || selectedImage.preview}
            alt={selectedImage.Label || selectedImage.name}
            sx={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
          />
        </Box>
      )}
    </>
  )
}

export default React.memo(FilePreviewCard)
